import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase";

const cleanId = (value) => String(value || "").trim().replace(/[^a-zA-Z0-9_-]/g, "-");

export const financialReportDocumentId = (companyId, periodKey) =>
  `${cleanId(companyId)}__${cleanId(periodKey)}`;

export const subscribeMonthlyFinancialReports = ({ tenantId, onData, onError }) => {
  if (!tenantId) {
    onData?.([]);
    return () => {};
  }

  const reportsQuery = query(
    collection(db, "tenants", tenantId, "financialReports"),
    orderBy("periodKey", "desc")
  );
  return onSnapshot(
    reportsQuery,
    (snapshot) => onData?.(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError
  );
};

const normalizeCompanyName = (value) => String(value || "")
  .trim()
  .toLocaleLowerCase("tr-TR")
  .replace(/[ıİ]/g, "i")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

export const findMonthlyFinancialReport = async ({
  tenantId,
  companyId = "",
  companyName = "",
  companyShortName = "",
  periodKey = ""
}) => {
  if (!tenantId || !periodKey) return null;
  const snapshot = await getDocs(collection(db, "tenants", tenantId, "financialReports"));
  const expectedNames = [companyName, companyShortName]
    .map(normalizeCompanyName)
    .filter(Boolean);

  const matched = snapshot.docs.find((item) => {
    const report = item.data();
    if (String(report.periodKey || "") !== String(periodKey)) return false;
    if (companyId && report.companyId && String(report.companyId) === String(companyId)) return true;
    const reportNames = [report.companyName, report.companyShortName, report.detectedCompanyName]
      .map(normalizeCompanyName)
      .filter(Boolean);
    return expectedNames.some((name) => reportNames.includes(name));
  });

  return matched ? { id: matched.id, ...matched.data() } : null;
};

export const saveMonthlyFinancialReport = async ({
  tenantId,
  company,
  periodKey,
  periodLabel,
  parsed,
  file,
  user
}) => {
  if (!tenantId) throw new Error("Müşteri çalışma alanı belirlenemedi.");
  if (!company?.id || !company?.name) throw new Error("Firma seçimi zorunludur.");
  if (!/^20\d{2}-(0[1-9]|1[0-2])$/.test(periodKey || "")) throw new Error("Geçerli bir rapor dönemi seçin.");
  if (!parsed || !file) throw new Error("İşlenecek Excel dosyası bulunamadı.");

  const reportId = financialReportDocumentId(company.id, periodKey);
  const sourcePath = `financialReports/${tenantId}/${cleanId(company.id)}/${reportId}/source.xlsx`;
  const uploaded = await uploadBytes(ref(storage, sourcePath), file, {
    contentType: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    customMetadata: {
      originalName: String(file.name || "Aylık Excel Bilgi Formu.xlsx").slice(0, 500),
      companyId: String(company.id),
      periodKey
    }
  });
  const sourceUrl = await getDownloadURL(uploaded.ref);

  const payload = {
    tenantId,
    companyId: company.id,
    companyName: company.name,
    companyShortName: company.shortName || company.name,
    periodKey,
    periodLabel,
    schemaVersion: parsed.schemaVersion || 1,
    sourceSheetName: parsed.sourceSheetName || "",
    detectedCompanyName: parsed.detectedCompanyName || "",
    projects: parsed.projects || [],
    offProject: parsed.offProject || {},
    exemptions: parsed.exemptions || [],
    trade: parsed.trade || [],
    funds: parsed.funds || {},
    summary: parsed.summary || {},
    rowCount: Number(parsed.rowCount || 0),
    sourceFile: {
      name: file.name,
      path: sourcePath,
      url: sourceUrl,
      size: file.size,
      contentType: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    },
    uploadedBy: user?.uid || "",
    uploadedByName: user?.displayName || user?.email || "",
    updatedAt: serverTimestamp()
  };

  await setDoc(doc(db, "tenants", tenantId, "financialReports", reportId), {
    ...payload,
    createdAt: serverTimestamp()
  }, { merge: true });

  return reportId;
};

export const deleteMonthlyFinancialReport = async ({ tenantId, report }) => {
  if (!tenantId || !report?.id) throw new Error("Silinecek rapor bulunamadı.");
  await deleteDoc(doc(db, "tenants", tenantId, "financialReports", report.id));

  if (report.sourceFile?.path) {
    try {
      await deleteObject(ref(storage, report.sourceFile.path));
    } catch (error) {
      if (error?.code !== "storage/object-not-found") throw error;
    }
  }
};
